import React from 'react';
import { motion } from 'motion/react';
import { getSignedMediaUrl } from '@/lib/utils';

interface EventInfoCardProps {
  /** Event image ID for subtle background */
  eventImageId?: string | null;
  /** Direct event image URL (use instead of eventImageId if URL is already computed) */
  eventImageUrl?: string | null;
  /** Border color class (default: border-gray-700/30) */
  borderColorClass?: string;
  /** Optional top-right action button */
  topRightAction?: React.ReactNode;
  /** Header section content (wrapped in glassmorphism header) */
  headerContent?: React.ReactNode;
  /** Optional content section (renders with bg-black/40) */
  contentSection?: React.ReactNode;
  /** 
   * Children for full flexibility - when provided, headerContent and contentSection are ignored.
   * Use this when you need complete control over the card's internal structure.
   */
  children?: React.ReactNode;
  /** Whether to animate on mount */
  animate?: boolean;
  /** Additional className for the container */
  className?: string;
}

/**
 * Reusable event info card container with glassmorphism styling.
 * Provides consistent card structure for cart items and transfer pages.
 * 
 * Usage modes:
 * 1. Structured mode: Pass headerContent and optionally contentSection
 * 2. Flexible mode: Pass children for complete control over card internals
 */
export const EventInfoCard: React.FC<EventInfoCardProps> = ({
  eventImageId,
  eventImageUrl,
  borderColorClass = 'border-gray-700/30',
  topRightAction,
  headerContent,
  contentSection,
  children,
  animate = true,
  className = '',
}) => {
  // Get event image URL - use direct URL if provided, otherwise compute from ID
  const hasEventImageId = eventImageId && eventImageId !== '00000000-0000-0000-0000-000000000000';
  const computedImageUrl = hasEventImageId ? getSignedMediaUrl(eventImageId, 'image').url : null;
  const finalImageUrl = eventImageUrl || computedImageUrl;

  const Wrapper = animate ? motion.div : 'div';
  const animationProps = animate
    ? { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } }
    : {};

  return (
    <Wrapper
      {...animationProps}
      className={`relative rounded-2xl overflow-hidden shadow-lg ${borderColorClass} border ${className}`}
      style={{
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      {/* Background Image - Subtle */}
      {finalImageUrl && (
        <div className="absolute inset-0 opacity-10">
          <img
            src={finalImageUrl}
            alt="Event"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="relative">
        {/* Top Right Action Button */}
        {topRightAction && (
          <div className="absolute top-4 right-4 z-10">
            {topRightAction}
          </div>
        )}

        {/* Flexible mode: render children directly */}
        {children ? (
          children
        ) : (
          /* Structured mode: use headerContent and contentSection */
          <>
            {/* Header Section - With glassmorphism */}
            {headerContent && (
              <div
                className="p-6 border-b border-gray-700/30"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                }}
              >
                {headerContent}
              </div>
            )}

            {/* Content Section - Dark background for add-ons etc */}
            {contentSection && (
              <div className="p-6 bg-black/40">
                {contentSection}
              </div>
            )}
          </>
        )}
      </div>
    </Wrapper>
  );
};

export default EventInfoCard;
